import React from 'react';
import { get, find, isEmpty, isEqual, cloneDeep, filter, indexOf } from 'lodash';

// local dependecies
import { toDoPrescription as ToDoPrescriptionView } from 'ui/views/widgets/toDoPrescription';
import { ToDoPrescriptionService } from 'services/toDoPrescription';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import * as errorOverlay from 'utils/errorOverlay';
import * as successOverlay from 'utils/successOverlay';
import { GLOBAL_LABELS } from 'constants/common';
import { getLabel } from 'utils/getLabel';
import { resolveSecondaryOptionsResolver } from 'resolvers/resolveSecondaryOptions';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { ToDoPrescription } from 'ui/containers/ToDoPrescription';
 *
 * // render
 * <ToDoPrescription propName='propValue'/>
 */
export class ToDoPrescription extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        this.state = this.composeStateFromProps();

        this._bind();

        this.toDoPrescriptionService = new ToDoPrescriptionService();

    }

    _bind() {
        this.onFormSubmit = this.onFormSubmit.bind( this );
        this.composeStateFromProps = this.composeStateFromProps.bind( this );
        this.handleFormChangeCallback = this.handleFormChangeCallback.bind( this );
    }

    composeStateFromProps() {
        log.debug( 'ToDoPrescription.composeStateFromProps()' );

        const commonFields = filter( this.props.fields, field => {
            return  -1 !== indexOf( this.props.commonFields, field.name );
        } );

        const primaryTypeField = find( commonFields, function( field ) { return 'primary' === get( field, 'config.fieldType', null ); } );
        const secondaryTypeField = find( commonFields, function( field ) { return 'secondary' === get( field, 'config.fieldType', null ); } );

        return {
            options: get( this.props, 'options', {} ),
            fields: commonFields,

            //fields: this.props.fields,
            values: get( this.props, 'state.values' ),
            primaryType: get( this.props, `values.${ primaryTypeField.name }`, undefined ),
            secondaryType: get( primaryTypeField, `values.${ secondaryTypeField.name }`, undefined ),
            staticFields: []
        };
    }

    async handleFormChangeCallback( { values, field } ) {
        log.debug( 'ToDoPrescription.handleFormChangeCallback()' );

        /*-------------------*/

        const primaryTypeField = find( this.props.fields, function( _field ) { return 'primary' === get( _field, 'config.fieldType', null ); } );
        const secondaryTypeField = find( this.props.fields, function( _field ) { return 'secondary' === get( _field, 'config.fieldType', null ); } );
        
        const primaryTypeValue = get( values, `${ primaryTypeField.name }`, undefined );


        // use resolver to resolve correct secondary field (type) options according to primary field (category) selection
        if( ! isEmpty( primaryTypeValue ) && ! isEqual( primaryTypeValue, this.state.primaryType ) ) {

            const { updatedOptions } = resolveSecondaryOptionsResolver( {
                values,
                options: this.props.options,
                primaryTypeField,
                secondaryTypeField,
                fields: this.state.fields
            } );

            // remove the previously selected values
            const _values = cloneDeep( values );
            values[ secondaryTypeField.name ] = '';

            this.setState( {
                values: _values,
                options: updatedOptions,
                primaryType: primaryTypeValue,
                secondaryType: values[ secondaryTypeField.name ],
                fields: filter( this.props.fields, _field => {
                    return  -1 !== indexOf( this.props.commonFields, _field.name );
                } )
            } );

        }

        if( isEqual( field.name, secondaryTypeField.name ) && ! isEqual( this.state.secondaryType, values[ `${secondaryTypeField.name}` ] ) ) {

            // fetch dynamicField object
            const dynamicField = find( this.props.dynamicFields, { value: values[ field.name ] } );

            if( get( dynamicField, 'fields', undefined ) && isEqual( dynamicField.value, values[ field.name ] ) ) {

                const newFields = filter( this.props.fields, _field => {
                    return  -1 !== indexOf( dynamicField.fields, _field.name );
                } );

                this.setState( {
                    fields: newFields,
                    values: values,
                    secondaryType: values[ `${secondaryTypeField.name}` ]
                } );

            } else {

                const newFields = filter( this.props.fields, _field => {
                    return  -1 !== indexOf( this.props.commonFields, _field.name );
                } );

                this.setState( {
                    fields: newFields,
                    values: values,
                    secondaryType: values[ `${secondaryTypeField.name}` ]
                } );
            }
        }

        /*-------------------*/

    }

    /**
     * action executed when user clicks on form submit
     */
    async onFormSubmit ( formData ) {
        log.debug( 'ToDoPrescription.onFormSubmit()' );

        const context = get( this.props, 'payload', {} );

        // close overlay
        this.props.onCancelClick();

        // show page preloader
        pageLevelPreloader.show();

        let newUrl = find( this.props.urlMap, { name: 'Inactivate Prescriptions' } );
        if( !isEqual( formData.type, newUrl.name ) ) {
            newUrl = find( this.props.urlMap, { name: 'Manual Order' } );
        }

        const { status } = await this.toDoPrescriptionService.submitFormData( {
            url: newUrl.url,
            data: {
                context,
                data: formData
            }
        } );

        // hide page preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            successOverlay.open( { message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ), } );

        } else {
            errorOverlay.open();
        }
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <ToDoPrescriptionView
                { ...this.props }
                { ...this.state }
                onFormSubmit={ this.onFormSubmit }
                onChangeCallBack={ this.handleFormChangeCallback }
            />
        );
    }
}

// set display name
ToDoPrescription.displayName = 'ToDoPrescription';

// set default props
ToDoPrescription.defaultProps = {};

// prop types
ToDoPrescription.propTypes = {};
